import { useTheme } from "@/components/theme-provider";
import { UI_THEMES, type UiTheme } from "@/lib/ui-theme";
import { cn } from "@/lib/utils";

interface DevThemeSwitchProps<Step extends string> {
  /** Optional step jumps (onboarding): exercise any screen without signing up. */
  steps?: readonly Step[];
  activeStep?: Step;
  onJump?: (step: Step) => void;
}

const MODES = ["light", "dark"] as const;

/**
 * Dev-only strip for the two theme axes (Classic/Lilac x light/dark) plus optional step jumps.
 * Replaces the old OnboardingDevStepRail. Only when `import.meta.env.DEV`, so staging builds
 * on a non-dev Vite mode never ship it. The visual check "both themes" (GRA-146 later adds
 * screenshots) is done by hand with this switch.
 */
export function DevThemeSwitch<Step extends string>({
  steps,
  activeStep,
  onJump,
}: DevThemeSwitchProps<Step>) {
  const { theme, setTheme, uiTheme, setUiTheme } = useTheme();
  if (!import.meta.env.DEV) return null;

  // Mirrors theme-provider.tsx's own system-theme resolution so the active button reflects
  // what is actually on screen, not just a "system" -> "light" guess.
  const mode =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;

  return (
    <div
      className="pointer-events-auto fixed bottom-3 right-3 z-[100] flex max-w-[calc(100vw-1.5rem)] flex-col gap-1.5 rounded-lg border border-amber-500/40 bg-zinc-950/95 p-2 text-zinc-100 shadow-lg backdrop-blur-sm"
      data-testid="dev-theme-switch"
    >
      <div className="flex items-center gap-1">
        <span className="mr-1 font-mono text-[10px] font-semibold uppercase tracking-wide text-amber-400/90">
          Dev
        </span>
        {UI_THEMES.map((candidate: UiTheme) => (
          <DevButton
            key={candidate}
            active={uiTheme === candidate}
            onClick={() => setUiTheme(candidate)}
          >
            {candidate}
          </DevButton>
        ))}
        <span className="mx-1 h-4 w-px bg-zinc-700" aria-hidden="true" />
        {MODES.map((candidate) => (
          <DevButton
            key={candidate}
            active={mode === candidate}
            onClick={() => setTheme(candidate)}
          >
            {candidate}
          </DevButton>
        ))}
      </div>
      {steps && steps.length > 0 && onJump ? (
        <div className="flex flex-wrap gap-1 border-t border-zinc-800 pt-1.5">
          {steps.map((step) => (
            <DevButton key={step} active={step === activeStep} onClick={() => onJump(step)}>
              {step}
            </DevButton>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DevButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded border px-1.5 py-1 font-mono text-[10px] leading-none transition-colors",
        active
          ? "border-amber-400 bg-amber-500/20 text-amber-100"
          : "border-zinc-600 bg-zinc-900 text-zinc-300 hover:border-zinc-500 hover:bg-zinc-800",
      )}
    >
      {children}
    </button>
  );
}
